import { MapGenFillLayoutId } from '@nova/types';

import { Clamp } from '../../math-utils';
import { createSeededRandom, type SeededRandom } from '../../seeded-random';
import { areBaseTilesFullyReachable, ensureBaseTilesReachable, topUpFillWithScatterStrips } from '../place-tile-lines';
import { countTilesWithValue } from '../tile-grid';
import type { GenerateFillLayoutOptions, TileLayoutResult } from '../tile-layout-types';

export interface FillLayoutContext {
  width: number;
  height: number;
  coverageCap: number;
  gapTiles: number;
  minPlacementSizeTiles: number;
  maxPlacementSizeTiles: number;
  seed: number;
  fillTileValue: number;
  interior: number;
  budget: number;
  grid: Uint8Array;
  rand: SeededRandom;
}

export function createFillLayoutContext(options: GenerateFillLayoutOptions): FillLayoutContext {
  const width = Math.max(3, Math.floor(options.widthTiles));
  const height = Math.max(3, Math.floor(options.heightTiles));
  const coverageCap = Clamp(options.fillTileCoverage, 0, 1);
  const gapTiles = Math.max(0, Math.floor(options.minimumOpenTileGap));
  const minPlacementSizeTiles = Math.max(1, Math.floor(options.minimumPlacementSizeTiles));
  const maxPlacementSizeTiles = Math.max(minPlacementSizeTiles, Math.floor(options.maximumPlacementSizeTiles));
  // eslint-disable-next-line no-bitwise
  const seed = options.seed >>> 0;
  const fillTileValue = Clamp(Math.floor(options.fillTileValue), 1, 255);
  const interior = width * height;
  const budget = Math.floor(interior * coverageCap);

  return {
    width,
    height,
    coverageCap,
    gapTiles,
    minPlacementSizeTiles,
    maxPlacementSizeTiles,
    seed,
    fillTileValue,
    interior,
    budget,
    grid: new Uint8Array(width * height),
    rand: createSeededRandom(seed)
  };
}

export function finalizeFillLayout(
  ctx: FillLayoutContext,
  layoutId: MapGenFillLayoutId,
  primaryFilled: number,
  scatterFilled: number
): TileLayoutResult {
  const reachabilityCarved = ensureBaseTilesReachable(ctx.grid, ctx.width, ctx.height);
  const filledTileCount = countTilesWithValue(ctx.grid, ctx.fillTileValue);
  const coverage = ctx.interior > 0 ? filledTileCount / ctx.interior : 0;
  const pass = filledTileCount <= ctx.budget && areBaseTilesFullyReachable(ctx.grid, ctx.width, ctx.height);

  return {
    grid: ctx.grid,
    stats: {
      layoutId,
      seed: ctx.seed,
      widthTiles: ctx.width,
      heightTiles: ctx.height,
      fillTileValue: ctx.fillTileValue,
      budget: ctx.budget,
      minimumOpenTileGap: ctx.gapTiles,
      minimumPlacementSizeTiles: ctx.minPlacementSizeTiles,
      maximumPlacementSizeTiles: ctx.maxPlacementSizeTiles,
      lineTileCount: primaryFilled,
      scatterTileCount: scatterFilled,
      filledTileCount,
      coverage,
      coverageCap: ctx.coverageCap,
      reachabilityCarved,
      pass
    }
  };
}

/** After a primary wall pass, spend leftover budget on scatter strips then finalize. */
export function topUpAndFinalize(
  ctx: FillLayoutContext,
  layoutId: MapGenFillLayoutId,
  primaryFilled: number
): TileLayoutResult {
  const scatterFilled = topUpFillWithScatterStrips(ctx.grid, ctx.width, ctx.height, ctx.budget, {
    gapTiles: ctx.gapTiles,
    minSizeTiles: ctx.minPlacementSizeTiles,
    maxSizeTiles: ctx.maxPlacementSizeTiles,
    fillValue: ctx.fillTileValue,
    rand: createSeededRandom(ctx.seed + 31337)
  });
  return finalizeFillLayout(ctx, layoutId, primaryFilled, scatterFilled);
}
